/* The pipeline board: five columns, and every card is a person somebody owes a call.
 *
 * WON AND LOST ARE NOT HERE. A won lead is a member and is read on the member record;
 * a lost one is on the win-back cadence. Both are reachable by searching. A board that
 * opens on them is a board of people nobody can act on, and the scope default in
 * LeadQuery is where that is decided rather than here.
 *
 * THE BOARD GROUPS ON ROUTING, NOT ON THE ANCHOR. "Whose call is this" is what a board
 * is for; "who actually knows them" is what the record is for, and a form submission
 * is not a relationship (SITE-RULES 2). See routedTo() in lib/leads.
 *
 * THE UNASSIGNED COLUMN IS THE POINT OF THE BY-COACH VIEW. It sorts first, ahead of
 * even the busiest coach, because it is the one column that is a question rather than
 * a workload.
 */
import { useMemo, useState } from 'react';
import type { Lead, LeadStage } from '@acrostic/model';
import { daysSince } from '@acrostic/model';
import { useAsync, useSource } from '../data/SourceProvider.js';
import { MAX_PAGE } from '../data/SeedSource.js';
import { NoneRow } from '../components/Panel.js';
import { Avatar } from '../components/Avatar.js';
import { OPEN_PATH, SOURCE_UNRECORDED, STAGES, routedTo, sourceText } from '../lib/leads.js';
import { fmt } from '../lib/fmt.js';
import { gymToday } from '../lib/today.js';
import { go } from '../routes.js';
import { useToast } from '../components/Toast.js';
import { NewLead } from './leads/NewLead.js';

/** How many cards a column shows before it offers the rest. */
const PAGE = 40;
const UNASSIGNED = '__none__';

/** New and contacted only: a lead in nurture is not stale at three days. */
const STALE_DAYS = 3;
function isStale(l: Lead): boolean {
  if (l.stage !== 'new' && l.stage !== 'contacted') return false;
  return (daysSince(l.createdAt, gymToday()) ?? 0) >= STALE_DAYS;
}

export function Leads({ me }: { me: { id: string; name: string; hasLeadBook: boolean } }) {
  const source = useSource();
  const toast = useToast();
  const [epoch, setEpoch] = useState(0);
  const [view, setView] = useState<'stage' | 'coach'>('stage');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [coachFilter, setCoachFilter] = useState('all');
  /* A seat with a book of its own OPENS on Mine, with Everyone one click away. A
     manager or an owner has no book, so there is nothing to switch. */
  const [mine, setMine] = useState(me.hasLeadBook);
  const [adding, setAdding] = useState(false);
  const [limits, setLimits] = useState<Record<string, number>>({});

  const { data, error } = useAsync(
    () => source.getLeads({ scope: 'open', size: MAX_PAGE }),
    [source, epoch],
  );

  const leads = useMemo(() => data?.items ?? [], [data]);

  /* Both pickers are built from who is ACTUALLY on this board. A source filter
     offering twelve sources when the board holds four, or a coach filter listing
     fifty-four trainers of whom fifty own nothing, is not a filter. */
  const sources = useMemo(() => {
    const seen = [...new Set(leads.map(sourceText))];
    /* "Not recorded" sorts last rather than alphabetically: it is the one entry that
       is an absence rather than a source. */
    return seen.sort((a, b) =>
      Number(a === SOURCE_UNRECORDED) - Number(b === SOURCE_UNRECORDED) || a.localeCompare(b));
  }, [leads]);

  const coaches = useMemo(
    () => [...new Set(leads.map(routedTo).filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b)),
    [leads],
  );

  const shown = useMemo(() => leads.filter((l) => {
    if (mine && l.ownerId !== me.id) return false;
    if (sourceFilter !== 'all' && sourceText(l) !== sourceFilter) return false;
    if (coachFilter === 'all') return true;
    const c = routedTo(l);
    return coachFilter === UNASSIGNED ? !c : c === coachFilter;
  }), [leads, mine, me.id, sourceFilter, coachFilter]);

  /* Whether any client-side filter is narrowing the board. It decides which numbers
     the header may state as facts about the pipeline: with a filter on, the board is
     a subset of a subset and only the cards can be counted. */
  const filtered = mine || sourceFilter !== 'all' || coachFilter !== 'all';
  const truncated = !!data && data.total > data.items.length;

  async function restage(id: string, stage: LeadStage) {
    const l = leads.find((x) => x.id === id);
    if (!l || l.stage === stage) return;
    await source.updateLead(id, { stage });
    toast(`${l.name} → ${STAGES[stage].short}`, 'good');
    setEpoch((n) => n + 1);
  }

  const columns: { key: string; label: string; items: Lead[]; droppable?: LeadStage }[] = [];
  if (view === 'stage') {
    for (const stage of OPEN_PATH) {
      columns.push({
        key: stage,
        label: STAGES[stage].short,
        droppable: stage,
        items: shown.filter((l) => l.stage === stage).sort(byRecency),
      });
    }
  } else {
    const byCoach = new Map<string, Lead[]>();
    for (const l of shown) {
      const c = routedTo(l) ?? UNASSIGNED;
      const list = byCoach.get(c);
      if (list) list.push(l); else byCoach.set(c, [l]);
    }
    const names = [...byCoach.keys()].sort((a, b) =>
      (a === UNASSIGNED ? -1 : b === UNASSIGNED ? 1 : 0)
      || (byCoach.get(b)!.length - byCoach.get(a)!.length)
      || a.localeCompare(b));
    for (const n of names) {
      columns.push({ key: 'coach:' + n, label: n === UNASSIGNED ? 'Unassigned' : n, items: byCoach.get(n)!.sort(byRecency) });
    }
  }

  return (
    <>
      <section className="panel">
        <header>
          <h2 className="t-heading-h3">Pipeline</h2>
          {/* THE HEADER IS ABOUT THE PIPELINE. THE BOARD IS ABOUT WHAT IS ON IT.
              Both counts come from the source, which counted them over every open
              lead, and never off `leads` - that is the page. At FNS the page is 100
              of 2,145 and this line read "100 open · 100 nobody has spoken to",
              which is a number nobody could have got wrong at seven leads and
              nobody could have believed at two thousand (SITE-RULES 3). */}
          {data && (
            <span className="eyebrow t-label-xsmall">
              {fmt.num(data.total)} open
              {data.unrouted ? ` · ${fmt.num(data.unrouted)} nobody has spoken to` : ''}
            </span>
          )}
        </header>

        <div className="filters">
          {me.hasLeadBook && (
            <div className="seg" role="group" aria-label="Whose leads">
              <button type="button" className={'segbtn t-label-small' + (mine ? ' on' : '')}
                onClick={() => setMine(true)}>Mine</button>
              <button type="button" className={'segbtn t-label-small' + (mine ? '' : ' on')}
                onClick={() => setMine(false)}>Everyone</button>
            </div>
          )}
          <span className="spacer" />
          <label className="t-caption muted">
            View{' '}
            <select className="t-label-small" value={view}
              onChange={(e) => setView(e.target.value as 'stage' | 'coach')}>
              <option value="stage">By stage</option>
              <option value="coach">By coach</option>
            </select>
          </label>
          <label className="t-caption muted">
            Coach{' '}
            <select className="t-label-small" value={coachFilter} onChange={(e) => setCoachFilter(e.target.value)}>
              <option value="all">All coaches</option>
              <option value={UNASSIGNED}>Unassigned only</option>
              {coaches.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="t-caption muted">
            Source{' '}
            <select className="t-label-small" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="all">All sources</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <button type="button" className="btn t-label-small" onClick={() => setAdding(true)}>+ New lead</button>
        </div>

        {error && <NoneRow>Could not read the pipeline. {error.message}</NoneRow>}
        {!error && !data && <NoneRow>Reading the pipeline…</NoneRow>}
        {/* Said once, plainly, so every count BELOW it can be read for what it is:
            a count of cards on the board. The column headings and both pickers are
            built from these cards and nothing else. */}
        {truncated && data && (
          <div className="t-caption muted">
            The board holds the {fmt.num(data.items.length)} most recent of {fmt.num(data.total)} open
            leads. The columns and filters below count only these.
          </div>
        )}
        {!truncated && filtered && data && (
          <div className="t-caption muted">
            Showing {fmt.num(shown.length)} of {fmt.num(data.total)} open leads.
          </div>
        )}
      </section>

      {data && (
        <div className="kanban">
          {columns.length === 0 && <NoneRow>No leads match these filters.</NoneRow>}
          {columns.map((col) => (
            <Column
              key={col.key} label={col.label} items={col.items}
              limit={limits[col.key] ?? PAGE}
              onMore={() => setLimits((l) => ({ ...l, [col.key]: (l[col.key] ?? PAGE) + PAGE }))}
              {...(col.droppable ? { onDrop: (id: string) => void restage(id, col.droppable!) } : {})}
            />
          ))}
        </div>
      )}

      {adding && (
        <NewLead
          onClose={() => setAdding(false)}
          onAdded={(l) => { setAdding(false); toast(`${l.name} added. First response within the hour, from a named person.`, 'good'); setEpoch((n) => n + 1); }}
        />
      )}
    </>
  );
}

function byRecency(a: Lead, b: Lead): number {
  return String(b.lastActivity ?? b.createdAt).localeCompare(String(a.lastActivity ?? a.createdAt));
}

function Column({ label, items, limit, onMore, onDrop }: {
  label: string; items: Lead[]; limit: number; onMore: () => void;
  onDrop?: (id: string) => void;
}) {
  const [over, setOver] = useState(false);
  /* Zero on a seat that is not sent values, and the head then carries no tooltip
     rather than one saying $0. */
  const value = items.reduce((a, l) => a + (l.valueCents ?? 0), 0);
  const visible = items.slice(0, limit);
  const hidden = items.length - visible.length;

  return (
    <div
      className={'kcol' + (over ? ' dragover' : '')}
      {...(onDrop ? {
        onDragOver: (e: React.DragEvent) => { e.preventDefault(); setOver(true); },
        onDragLeave: () => setOver(false),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault(); setOver(false);
          const id = e.dataTransfer.getData('text/lead');
          if (id) onDrop(id);
        },
      } : {})}
    >
      {/* The column's monthly value rides on the head's tooltip: five heads across at
          1440 have room for a name and a count on one line, and nothing more. */}
      <div className="khead t-label-medium" title={value ? `${fmt.num(items.length)} · ${fmt.money(value)}/mo` : undefined}>
        {label}<span className="kcount t-label-small">{fmt.num(items.length)}</span>
      </div>
      {visible.map((l) => <Card key={l.id} l={l} draggable={!!onDrop} />)}
      {hidden > 0 && (
        <div className="kmore-wrap">
          <button type="button" className="btn t-label-small" onClick={onMore}>
            Load {fmt.num(Math.min(PAGE, hidden))} more
          </button>
          <span className="t-caption muted">{fmt.num(hidden)} hidden</span>
        </div>
      )}
    </div>
  );
}

function Card({ l, draggable }: { l: Lead; draggable: boolean }) {
  const days = daysSince(l.createdAt, gymToday()) ?? 0;
  const coach = routedTo(l);
  const spoken = !!l.anchor;
  return (
    <button
      type="button" className="kcard" draggable={draggable}
      onClick={() => go('/leads/' + l.id)}
      onDragStart={(e) => e.dataTransfer.setData('text/lead', l.id)}
    >
      <span className="kh">
        <Avatar name={l.name} photoUrl={l.photoUrl} size="md" />
        <span className="kn t-label-large">{l.name}</span>
      </span>
      {/* Where they came from. What they would be WORTH used to sit here too, and
          a support coach who chases the 549 harder than the 199 is making exactly
          the distinction this product argues against. It is null on this seat now
          and the sentence does not go looking for it. */}
      <span className="km t-caption muted">
        {[sourceText(l), l.valueCents == null ? null : fmt.money(l.valueCents) + '/mo intent']
          .filter(Boolean).join(' · ')}
      </span>
      {/* Routing is the desk's default, not a relationship, so a routed lead nobody
          has spoken to names whose call it is and says so, quietly. */}
      <span className="km t-caption">
        {coach
          ? spoken ? coach : <span className="muted">Routed to {coach}</span>
          : <span className="muted">Unassigned</span>}
      </span>
      <span className="kt">
        <span className="badge t-label-xsmall">{days === 0 ? 'today' : days + 'd'}</span>
        {isStale(l) && <span className="badge risk t-label-xsmall">needs touch</span>}
      </span>
    </button>
  );
}
